import { useState } from 'react';
import { submitProfile } from '@core/firebase/profile';
import type { Gender } from '@core/firebase/profile';
import { parsedDraftOf, validateProfileDraft, type ProfileProblem } from '@core/auth/profileDraft';
import { useLanguage } from '@/contexts/LanguageContext';
import { AuthLayout, authButton, authField } from '@/components/AuthLayout';
import { cn } from '@/lib/cn';
import { applyFormat, inputAttrs } from '@core/format/inputs';

/**
 * What someone tells the app about themselves before joining a session.
 *
 * Validation is the shared validator, not a second copy: the age floor is a
 * product rule the Firestore rules also enforce, and two implementations of it
 * would eventually disagree. The problem it returns carries its own field, so
 * a message can never appear under the wrong box.
 */
export default function CompleteProfilePage() {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [problem, setProblem] = useState<ProfileProblem | null>(null);
  const [busy, setBusy] = useState(false);

  const errorFor = (field: ProfileProblem['field']) =>
    problem && problem.field === field ? t(problem.key) : null;

  const save = async () => {
    if (busy) return;
    const values = { name, age, gender };
    const found = validateProfileDraft(values);
    if (found) {
      setProblem(found);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await submitProfile(parsedDraftOf(values));
      // Landing the Firestore write is what moves this page out of the way —
      // identity.ts is already listening for it. Nothing local decides that.
    } catch {
      // A write failure belongs to no single field; it is shown under the name
      // so it is not lost, and save is the thing to press again.
      setProblem({ field: 'name', key: 'auth.nameStep.failed' });
    } finally {
      setBusy(false);
    }
  };

  const label = 'block text-[15px] font-medium text-ink';

  return (
    // The second half of signing up, so it wears the same frame as the first —
    // a bare full-width form here would read as a different app between the
    // code and the name.
    <AuthLayout cardTestId="profile-card" slide="journey">
      <header>
        <h1 className="text-[32px] font-semibold tracking-tight text-ink lg:text-[34px]">{t('auth.nameStep.title')}</h1>
        <p className="mt-1.5 text-base text-helper">{t('auth.nameStep.subtitle')}</p>
      </header>

      <div className="mt-8 space-y-5">
        <label className="block space-y-2">
          <span className={label}>{t('auth.nameStep.label')}</span>
          <input
            data-testid="name-input"
            value={name}
            onChange={(e) => setName(applyFormat('personName', e.target.value))}
            {...inputAttrs('personName')}
            placeholder={t('auth.nameStep.placeholder')}
            className={cn(authField, errorFor('name') && 'border-danger')}
          />
          {errorFor('name') && (
            <p data-testid="name-error" role="alert" className="text-sm text-danger">{errorFor('name')}</p>
          )}
        </label>

        <label className="block space-y-2">
          <span className={label}>{t('auth.nameStep.ageLabel')}</span>
          <input
            data-testid="age-input"
            value={age}
            onChange={(e) => setAge(applyFormat('age', e.target.value))}
            placeholder={t('auth.nameStep.agePlaceholder')}
            {...inputAttrs('age')}
            className={cn(authField, errorFor('age') && 'border-danger')}
          />
          {errorFor('age') && (
            <p data-testid="age-error" role="alert" className="text-sm text-danger">{errorFor('age')}</p>
          )}
        </label>

        {/* Sessions are expected to pair same-gender, so this is a value the
            matching depends on rather than a demographic field. */}
        <fieldset className="space-y-2">
          <legend className={cn(label, 'mb-2')}>{t('auth.nameStep.genderLabel')}</legend>
          <div className="flex gap-1 rounded-full bg-surface p-1">
            {(['male', 'female'] as const).map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`gender-${g}`}
                aria-pressed={gender === g}
                onClick={() => setGender(g)}
                className={cn(
                  'h-12 flex-1 rounded-full text-base transition-colors',
                  gender === g
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : 'text-helper hover:text-ink',
                )}
              >
                {t(`auth.nameStep.${g}`)}
              </button>
            ))}
          </div>
          {errorFor('gender') && (
            <p data-testid="gender-error" role="alert" className="text-sm text-danger">{errorFor('gender')}</p>
          )}
          {/* Optional since App Review 5.1.1(v) — saying why it is asked at all
              is what makes an optional question honest. */}
          <p data-testid="gender-why" className="text-sm leading-relaxed text-helper">{t('auth.nameStep.genderWhy')}</p>
        </fieldset>

        <button
          type="button"
          data-testid="save-profile"
          onClick={save}
          disabled={busy}
          className={cn(authButton, 'mt-2')}
        >
          {t('auth.nameStep.submit')}
        </button>
      </div>
    </AuthLayout>
  );
}
